import React, { useState } from 'react';

import { FormComponent, PushComponent, UiComponent } from 'components';
import { ContentType, EventItemType, IContent } from 'types';
import { ContentsQuery, FileServiceQuery } from 'queries';
import useModal from 'hooks/useModal';
import { useFileService } from 'hooks';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import * as S from './style';

interface Props {
  content?: IContent | null;
}

function ContentsForm({ content }: Props) {
  const { handleClose, handleAlert } = useModal();
  const { mutateAsync: create } = ContentsQuery.useCreate();
  const { mutateAsync: update } = ContentsQuery.useUpdate();
  const { handleUploadd: upload } = FileServiceQuery.useUpload();
  const { fileUrl, file, handleFile } = useFileService({
    initFileUrl: content?.thumbnailUrl,
  });
  const [organizationId, setOrganizationId] = useState<
    number | null | undefined
  >(content?.organizationId);
  const [type, setType] = useState<ContentType>(
    content?.type ?? ContentType.INTERNAL,
  );
  const [selectedEvent, setSelectedEvent] = useState<EventItemType | null>(
    null,
  );
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const eventId =
    selectedEvent?.id ??
    (content?.type === ContentType.EVENT_DETAIL && Number(content.data)
      ? Number(content.data)
      : null);

  const handleSubmit = async (data: IContent) => {
    if (!fileUrl) {
      handleAlert('대표이미지를 업로드해주세요');
      return;
    }

    if (!content && !organizationId) {
      handleAlert('조직을 선택해주세요');
      return;
    }

    if (type === ContentType.EVENT_DETAIL) {
      if (!eventId) {
        handleAlert('연결할 봉사를 선택해주세요');
        return;
      }
      data.data = String(eventId);
    }

    if (file) {
      const res = await upload(file);
      if (!res) return;
      data.thumbnailUrl = res.data.url;
    } else {
      data.thumbnailUrl = fileUrl;
    }

    try {
      if (content) {
        await update({
          ...data,
          id: content.id,
          isNewFile: !!file,
        });
        toast.success('콘텐츠가 수정되었습니다.');
      } else {
        await create({ ...data, organizationId: organizationId ?? null });
        toast.success('콘텐츠가 등록되었습니다.');
      }
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
      return;
    } finally {
      handleClose();
    }
  };

  return (
    <>
      <FormComponent.Form css={S.form} submit={handleSubmit}>
        <div css={S.body}>
          {!content && (
            <>
              <p>조직</p>
              <FormComponent.OrganizationSelect
                permission="content:create"
                value={organizationId}
                onChange={setOrganizationId}
              />
            </>
          )}
          <p>콘텐츠명</p>
          <FormComponent.Input name="title" defaultValue={content?.title} />
          <p>서브콘텐츠명</p>
          <FormComponent.Input
            name="subTitle"
            defaultValue={content?.subTitle}
          />
          <p>상세내용</p>
          <FormComponent.Textarea
            name="description"
            defaultValue={content?.description}
          />

          {type !== ContentType.EVENT_DETAIL && (
            <>
              <p>Data</p>
              <FormComponent.Input name="data" defaultValue={content?.data} />
            </>
          )}

          <p>대표 이미지</p>
          <UiComponent.FileUpload
            name="thumbnailUrl"
            fileUrl={fileUrl}
            handleFile={handleFile}
          />

          <p>콘텐츠 유형</p>
          <FormComponent.Select
            name="type"
            options={[
              {
                name: '상세페이지',
                value: ContentType.INTERNAL,
              },
              {
                name: '외부페이지',
                value: ContentType.EXTERNAL,
              },
              {
                name: '유튜브',
                value: ContentType.YOUTUBE,
              },
              {
                name: '인스타그램',
                value: ContentType.INSTAGRAM,
              },
              {
                name: '봉사 상세',
                value: ContentType.EVENT_DETAIL,
              },
              {
                name: '설정안함',
                value: ContentType.NONE,
              },
            ]}
            value={type}
            handleChange={setType}
          />
          {type === ContentType.EVENT_DETAIL && (
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '0 8px',
                marginTop: '8px',
              }}
            >
              <UiComponent.Button
                type="button"
                variant="border"
                color="primary"
                css={{ height: '34px' }}
                onClick={() => setIsEventModalOpen(true)}
              >
                봉사 선택
              </UiComponent.Button>
              <span css={{ fontSize: '13px' }}>
                {selectedEvent
                  ? `#${selectedEvent.id} ${selectedEvent.title}`
                  : eventId
                    ? `#${eventId}`
                    : '선택된 봉사 없음'}
              </span>
            </div>
          )}
        </div>
        <div css={S.footer}>
          <UiComponent.Button type="submit" variant="contained" color="primary">
            {content === null ? '등록하기' : '수정하기'}
          </UiComponent.Button>
        </div>
      </FormComponent.Form>
      {isEventModalOpen && (
        <PushComponent.EventSelect
          onSelect={(event) => {
            setSelectedEvent(event);
            setIsEventModalOpen(false);
          }}
          onClose={() => setIsEventModalOpen(false)}
        />
      )}
    </>
  );
}

export default ContentsForm;
